import type { CSSProperties } from 'react';
import { Col, Flex, Grid, Row, Typography, theme } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import UnavailableZeroStateEcho from './UnavailableZeroStateEcho';
import { DataLevelTabs, useDataLevelTabs } from './DataLevelContainer';
import FilterCardEcho from '@/components/cards/FilterCard/FilterCardEcho';
import { VisibleFilterKey } from '@/components/cards/FilterCard/Filters';
import { ContentPerformanceDownloadDrawerTriggerEcho, NetworkProfileCardEcho } from '@/components';
import ContentPerformanceOverviewCardEcho from './ContentPerformanceOverviewCard/indexEcho';
import { useContentPerformanceOverviewQuery } from './useContentPerformanceOverviewQuery';
import { useContentPerformanceLiveDownloadsQuery } from './useContentPerformanceLiveDownloadsQuery';
import { useDerivedNetworkContext } from '@/context';
import {
  CONTENT_PERFORMANCE_DOWNLOAD_BUTTON_FLAG,
  getApplePodcastsOnlyPlayerSections,
  hasHlsEnabledShow,
  PODCAST_PLAYERS,
} from '@/utils';
import { useSyncUrlToFilters } from '@/hooks/useSyncUrlToFilters';
import useHasHlsEnabledNetwork from '@/hooks/useHasHlsEnabledNetwork';
import LiveDownloadsCard from './LiveDownloadsCard';
import { useFilterNavigation } from '@/hooks';

function ContentPerformanceOverviewTooltipContent({
  title,
  description,
  textStyle,
  gap,
}: {
  title: string;
  description: string;
  textStyle: CSSProperties;
  gap: number;
}) {
  return (
    <Flex vertical gap={gap}>
      <Typography.Text strong style={textStyle}>
        {title}
      </Typography.Text>
      <Typography.Text style={textStyle}>{description}</Typography.Text>
      <Typography.Text style={textStyle}>
        The percentage shows the change compared to the previous equivalent time period
      </Typography.Text>
    </Flex>
  );
}

const ContentPerformancePageEcho = () => {
  const { Title, Text } = Typography;
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMediumScreen = Boolean(screens.md);
  const tooltipInverseTextStyle: CSSProperties = { color: token.colorTextLightSolid };
  const {
    contentPerformance = false,
    contentPerformanceHls = false,
    contentPerformanceLiveDownloads = false,
    [CONTENT_PERFORMANCE_DOWNLOAD_BUTTON_FLAG]: isContentPerformanceDownloadButtonEnabled = false,
  } = useFlags<{
    contentPerformance?: boolean;
    contentPerformanceHls?: boolean;
    contentPerformanceLiveDownloads?: boolean;
    contentPerformanceDownloadButton?: boolean;
  }>();
  const hasHlsNetwork = useHasHlsEnabledNetwork();
  const isContentPerformanceEnabled =
    contentPerformance || (contentPerformanceHls && hasHlsNetwork);
  const { activeView, handleViewChange } = useDataLevelTabs();
  const { shows, selectedNetworks } = useDerivedNetworkContext();
  const noOfShows = shows.length;
  const { isSynced } = useSyncUrlToFilters({
    requireSingleShow: false,
    multiShowMode: true,
  });
  const { currentParams } = useFilterNavigation();
  const hasHlsInSelection = hasHlsEnabledShow(selectedNetworks, currentParams.showIds);

  const overviewQuery = useContentPerformanceOverviewQuery({
    enabled: isContentPerformanceEnabled && isSynced,
    dataSource: 'v3',
  });
  const liveDownloadsQuery = useContentPerformanceLiveDownloadsQuery({
    enabled: isContentPerformanceEnabled && isSynced && contentPerformanceLiveDownloads,
  });

  const visibleFilters: VisibleFilterKey[] =
    noOfShows > 1
      ? [VisibleFilterKey.Networks, VisibleFilterKey.Shows, VisibleFilterKey.DateRange]
      : [VisibleFilterKey.Networks, VisibleFilterKey.DateRange];

  return (
    <>
      {!isContentPerformanceEnabled ? (
        <Flex
          vertical
          align="center"
          style={{
            marginTop: token.marginXL + token.marginXS,
            width: '100%',
          }}
        >
          <UnavailableZeroStateEcho />
        </Flex>
      ) : (
        <>
          <Flex
            vertical={!isMediumScreen}
            justify="space-between"
            style={{
              width: '100%',
              paddingTop: token.paddingLG,
              paddingBottom: token.marginXL + token.marginXS,
            }}
          >
            <Title level={1} style={{ marginBottom: 0 }}>
              <FormattedMessage defaultMessage="Content performance" />
            </Title>
            {isContentPerformanceDownloadButtonEnabled && (
              <Flex justify="flex-end" style={{ width: isMediumScreen ? undefined : '100%' }}>
                <ContentPerformanceDownloadDrawerTriggerEcho />
              </Flex>
            )}
          </Flex>

          <FilterCardEcho
            visibleFilters={visibleFilters}
            onDataLevelFilterChange={(view) => handleViewChange(view, 'filter')}
          />

          <Row gutter={[token.marginLG, token.marginLG]} style={{ marginBottom: token.marginLG }}>
            <Col span={24}>
              <NetworkProfileCardEcho visibleFilters={visibleFilters} />
            </Col>
          </Row>

          {contentPerformanceLiveDownloads && (
            <Row gutter={[token.marginLG, token.marginLG]} style={{ marginBottom: token.marginLG }}>
              <Col xs={24}>
                <LiveDownloadsCard queryResult={liveDownloadsQuery} />
              </Col>
            </Row>
          )}

          <Row gutter={[token.marginLG, token.marginLG]} style={{ marginBottom: token.marginLG }}>
            {hasHlsInSelection && (
              <Col xs={24} lg={8}>
                <ContentPerformanceOverviewCardEcho
                  style={{ height: '100%', width: '100%' }}
                  label="Audio and Video"
                  queryResult={overviewQuery}
                  selectMetric={(d) => ({
                    value: d.plays,
                    valueDiff: d.plays_diff,
                  })}
                  playerSections={[{ players: PODCAST_PLAYERS }]}
                  tooltipTitle={
                    <ContentPerformanceOverviewTooltipContent
                      title="Audio downloads & Video views"
                      description="Total number of times the show was downloaded (audio) and viewed (video) during the selected period."
                      textStyle={tooltipInverseTextStyle}
                      gap={token.marginXS}
                    />
                  }
                  podcastPlayersTooltipContent={
                    <Flex vertical gap={token.marginXS}>
                      <Typography.Text style={tooltipInverseTextStyle}>Includes:</Typography.Text>
                      <ul
                        style={{
                          margin: 0,
                          paddingInlineStart: token.paddingLG,
                          color: token.colorTextLightSolid,
                          listStyleType: 'disc',
                        }}
                      >
                        <li style={{ marginBottom: token.marginXXS }}>
                          <Typography.Text style={tooltipInverseTextStyle}>
                            <Typography.Text strong style={tooltipInverseTextStyle}>
                              all podcast apps or websites
                            </Typography.Text>{' '}
                            people use to listen to the audio version of your show (Spotify, Apple
                            Podcasts, Castbox, Amazon Music, Pocket Casts, etc.)
                          </Typography.Text>
                        </li>
                        <li>
                          <Typography.Text style={tooltipInverseTextStyle}>
                            Apple Podcasts only for views of the video version of your show.
                          </Typography.Text>
                        </li>
                      </ul>
                    </Flex>
                  }
                />
              </Col>
            )}
            <Col xs={24} lg={hasHlsInSelection ? 8 : 24}>
              <ContentPerformanceOverviewCardEcho
                style={{ height: '100%', width: '100%' }}
                label="Audio downloads"
                queryResult={overviewQuery}
                selectMetric={(d) => ({
                  value: d.listens,
                  valueDiff: d.listens_diff,
                })}
                playerSections={[{ players: PODCAST_PLAYERS }]}
                tooltipTitle={
                  <ContentPerformanceOverviewTooltipContent
                    title="Audio downloads"
                    description="Total number of times the audio version of this show's episodes were downloaded during the selected period."
                    textStyle={tooltipInverseTextStyle}
                    gap={token.marginXS}
                  />
                }
                podcastPlayersTooltipContent={
                  <Text style={tooltipInverseTextStyle}>
                    Includes{' '}
                    <Text strong style={tooltipInverseTextStyle}>
                      all audio podcast apps or websites
                    </Text>{' '}
                    listeners use to play the audio version of your content (Spotify, Apple
                    Podcasts, Castbox, Amazon Music, Pocket Casts, etc.)
                  </Text>
                }
              />
            </Col>
            {hasHlsInSelection && (
              <Col xs={24} lg={8}>
                <ContentPerformanceOverviewCardEcho
                  style={{ height: '100%', width: '100%' }}
                  label="Video views"
                  queryResult={overviewQuery}
                  selectMetric={(d) => ({
                    value: d.views,
                    valueDiff: d.views_diff,
                  })}
                  playerSections={getApplePodcastsOnlyPlayerSections()}
                  tooltipTitle={
                    <ContentPerformanceOverviewTooltipContent
                      title="Video views"
                      description="Total number of times the video version of this show's episodes were viewed during the selected period."
                      textStyle={tooltipInverseTextStyle}
                      gap={token.marginXS}
                    />
                  }
                  podcastPlayersTooltipContent={
                    <Typography.Text style={tooltipInverseTextStyle}>
                      Video data reflects Apple Podcasts only, with more comprehensive data to come
                      in the future.
                    </Typography.Text>
                  }
                />
              </Col>
            )}
          </Row>

          <Row gutter={[token.marginLG, token.marginLG]}>
            <Col span={24}>
              <DataLevelTabs
                activeView={activeView}
                onViewChange={handleViewChange}
                isSynced={isSynced}
                showVideoMetrics={hasHlsInSelection}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default ContentPerformancePageEcho;
